import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProposalProps, ProposalType } from '~/data/proposals';

import { LegacyProposalAPI } from '../data';
import { EmployeeProposalUpdateCallback } from '../Forms/Common';

import { deleteLegacyProposal } from './data';
import { useSxStyles } from './useStyles';

type PropsType = {
  proposal: ProposalProps;
  proposalUpdateCallback: EmployeeProposalUpdateCallback;
};
export const EmployeeProposalDeleteButton = (props: PropsType) => {
  const { proposal, proposalUpdateCallback } = props;
  const sxStyles = useSxStyles();

  const [open, setOpen] = useState(false);
  const [checkbox, setCheckbox] = useState(false);

  const { t } = useTranslation();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCheckbox(false);
  };

  const handleCheckboxChange = () => {
    setCheckbox(!checkbox);
  };

  const useProposalDelete = async () => {
    try {
      await useProposalDeleteCall(proposal);
    } catch (error) {
      console.log(error);
    } finally {
      proposalUpdateCallback();
      handleClose();
    }
  };

  return (
    <Box>
      <Button
        variant="outlined"
        color="error"
        size="large"
        startIcon={<DeleteIcon />}
        onClick={(event) => {
          event.stopPropagation();
          handleClickOpen();
        }}
      >
        {t('laDelete')}
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {t('laConfirmDeleteProposal')}
        </DialogTitle>
        <DialogContent sx={sxStyles.dialogContent}>
          <DialogContentText id="alert-dialog-description">
            {t('laConfirmDeleteProposalDetails')}
          </DialogContentText>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox value={checkbox} onChange={handleCheckboxChange} />
              }
              label={t('laConfirmAndProceed')}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={useProposalDelete}
            disabled={!checkbox}
          >
            {t('laDelete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function useProposalTypeAPIMapper(proposalType: ProposalType) {
  switch (proposalType) {
    case ProposalType.ALLOWANCE_PROPOSAL:
      return LegacyProposalAPI.AllowanceProposal;
    case ProposalType.BENEFIT_PROPOSAL:
      return LegacyProposalAPI.BenefitProposal;
    case ProposalType.BILLABLE_HOURS:
      return LegacyProposalAPI.BillableHoursProposal;
    case ProposalType.BONUS_PROPOSAL:
      return LegacyProposalAPI.BonusProposal;
    case ProposalType.SALARY_PROPOSAL:
      return LegacyProposalAPI.SalaryProposal;
    default:
      return LegacyProposalAPI.AllowanceProposal;
  }
}

async function useProposalDeleteCall(proposal: ProposalProps) {
  const {
    proposalType,
    targetId: employeeId,
    proposalId,
    proposalData: { employmentFK: employmentId },
  } = proposal;

  const proposalAPI = useProposalTypeAPIMapper(proposalType);

  await deleteLegacyProposal(proposalAPI, {
    employeeId,
    employmentId,
    proposalId,
  });
}
